/* eslint-disable max-len */

import { useTranslation } from 'react-i18next';
import {
  capitalizeFirstLetterEveryword
} from '../../../shared/stringFunctions';
import { TXT_ADD_INFILL_MINERAL, TXT_INFILL_MINERALS } from '../../../translations/translationConstants';
import {
  TXT_SEARCH_BY_NAME
} from '../../../translations/translationConstants';
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import {
  addInfillMineralInput,
  deleteInfillMineralInput,
  getInfillMineralsInput,
  getInfillMineralOutput,
  GET_INFILL_MINERAL,
  updateInfillMineralInput
} from '../../../api';
import { useEffect } from 'react';
import { useState } from 'react';
import { ManageInfillMineralsTable } from './ManageInfillMineralsTable';
import {
  DeleteConfirmation,
  ExportToExcel,
  globalPageSizeOptions,
  PaginationBar
} from '../../../components';
import { infillMineral } from '../../../entityModel';
import { useApolloErrorHandler, useAuthServices } from '../../../hooks';
import {
  ADD_INFILL_MINERAL,
  DELETE_INFILL_MINERAL,
  UPDATE_INFILL_MINERAL
} from '../../../api';
import { ManageInfillMineralEditor } from './ManagerInfillMineralEditor';

export const ManageInfillMinerals = () => {
  const { t } = useTranslation();
  const { isSignedIn } = useAuthServices();

  const [txtSearch, setTxtSearch] = useState('');
  const [alterationData, setAlterationData] = useState<infillMineral[] | undefined>([]);

  const onTxtSearchChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTxtSearch(event.currentTarget.value);
  };

  const [selectedInfillMineral, setSelectedInfillMineral] = useState<infillMineral | undefined>(undefined);
  const [getInfillMinerals, { loading, data, error, refetch }] = useLazyQuery<getInfillMineralOutput>(GET_INFILL_MINERAL);
  const [getAllAlterations] = useLazyQuery<getInfillMineralOutput>(GET_INFILL_MINERAL, {
    onCompleted: (data) => setAlterationData(data.infillMinerals.items)
  });

  useEffect(() => {
    getInfillMinerals();
    getAllAlterations();
  }, []);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(globalPageSizeOptions[0]);

  const [openEditor, setOpenEditor] = useState(false);
  const [savingError, setSavingError] = useState<string | undefined>(undefined);

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);

  const lblSearch = capitalizeFirstLetterEveryword(t(TXT_SEARCH_BY_NAME));

  const fetchInfillMinerals = () => {
    const variables: getInfillMineralsInput = {
      code: txtSearch,
      take: pageSize,
      skip: (pageNumber - 1) * pageSize
    };
    getInfillMinerals({
      variables
    });
  };

  const fetch = (resetPage?: boolean) => {
    if (!!resetPage && pageNumber !== 1) {
      setPageNumber(1);
    } else {
      fetchInfillMinerals();
    }
  };

  useEffect(() => {
    useApolloErrorHandler(isSignedIn, error);
  }, [error]);

  useEffect(() => {
    fetch();
  }, [pageNumber]);

  useEffect(() => {
    fetch(true);
  }, [pageSize, txtSearch]);

  const openEditInfillMineralEditor = (infillMineral: infillMineral) => {
    setSelectedInfillMineral(infillMineral);
    setOpenEditor(true);
  };

  const openDeleteInfillMineralConfirmation = (infillMineral: infillMineral) => {
    setSelectedInfillMineral(infillMineral);
    setOpenDeleteConfirmation(true);
  };

  const openAddInfillMineralEditor = () => {
    setSelectedInfillMineral(undefined);
    setOpenEditor(true);
  };

  const closeEditor = () => {
    setOpenEditor(false);
  };

  const afterAddingOrUpdatingInfillMineral = () => {
    setOpenEditor(false);
    if (refetch) {
      refetch();
    }
  };

  const handleAddingOrUpdatingInfillMineralError = (error: ApolloError) => {
    if (error.graphQLErrors.length > 0 &&
      !!error.graphQLErrors[0].extensions) {
      setSavingError(error.graphQLErrors[0].extensions['message']);
    } else {
      setSavingError(error.message);
    }
  };

  const [addInfillMineral, addInfillMineralOperation] = useMutation<infillMineral>(ADD_INFILL_MINERAL, {
    onError: (e) => handleAddingOrUpdatingInfillMineralError(e),
    onCompleted: (result) => afterAddingOrUpdatingInfillMineral()
  });

  const [updateInfillMineral, updateInfillMineralOperation] = useMutation<infillMineral>(UPDATE_INFILL_MINERAL, {
    onError: (e) => handleAddingOrUpdatingInfillMineralError(e),
    onCompleted: (result) => afterAddingOrUpdatingInfillMineral()
  });

  const onEditorSave = (payload: addInfillMineralInput | updateInfillMineralInput) => {
    if (!selectedInfillMineral) {
      addInfillMineral({
        variables: {
          input: payload
        }
      });
    } else {
      updateInfillMineral({
        variables: {
          input: payload
        }
      });
    }
  };

  const onDeleteCancel = () => setOpenDeleteConfirmation(false);

  const [deleteInfillMineral, deleteInfillMineralOperation] = useMutation<boolean>(DELETE_INFILL_MINERAL, {
    onError: (e) => { },
    onCompleted: (result) => {
      if (data && refetch) {
        if (data.infillMinerals.items.length == 1) {
          const targetPage = pageNumber - 1 <= 0 ? 1 : pageNumber - 1;
          if (pageNumber !== targetPage) {
            setPageNumber(targetPage);
          } else {
            refetch();
          }
        } else {
          refetch();
        }
      }
      onDeleteCancel();
    }
  });

  const onDelete = () => {
    const input: deleteInfillMineralInput = {
      id: selectedInfillMineral!.id!
    };
    deleteInfillMineral({
      variables: {
        input
      }
    });
  };

  const onPageSizeChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPageSize(+event.target.value);
  };

  return (
    <div className="content-wrapper">
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>{t(capitalizeFirstLetterEveryword(TXT_INFILL_MINERALS))}</h1>
            </div>
          </div>
        </div>
      </section>
      <section className="content">
        <div className="container-fluid">
          {
            openEditor &&
            <ManageInfillMineralEditor
              infillMineral={selectedInfillMineral}
              onClose={closeEditor}
              onSave={onEditorSave}
              isSaving={
                !selectedInfillMineral ? addInfillMineralOperation.loading :
                  updateInfillMineralOperation.loading
              }
              savingError={savingError}
            />
          }
          {
            openDeleteConfirmation &&
            <DeleteConfirmation
              loading={deleteInfillMineralOperation.loading}
              onYes={onDelete}
              onNo={onDeleteCancel}
              objectName={selectedInfillMineral?.label || ''}
            />
          }
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <div className='row'>
                    <div className='col-6'>
                      <div className="form-group">
                        <label>{lblSearch}</label>
                        <input type='text' className='form-control' value={txtSearch} onChange={onTxtSearchChanged} />
                      </div>
                    </div>
                  </div>
                  <div className='row'>
                    <div className='col-md-6'>
                      <button
                        className='btn btn-primary'
                        data-target="#modal-infillMineral-editor"
                        data-toggle="modal"
                        disabled={loading}
                        onClick={openAddInfillMineralEditor}>
                        {t(TXT_ADD_INFILL_MINERAL).toUpperCase()}
                      </button>
                    </div>
                    <div className="col-md-6" style={{ textAlign: 'right' }}>
                      <ExportToExcel filename={TXT_INFILL_MINERALS} data={alterationData} />
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <ManageInfillMineralsTable
                    loading={loading}
                    data={data}
                    error={error}
                    onEdit={openEditInfillMineralEditor}
                    onDelete={openDeleteInfillMineralConfirmation}
                  />
                </div>
                <PaginationBar
                  currentPage={pageNumber}
                  totalCount={data?.infillMinerals.totalCount}
                  pageSize={pageSize}
                  onPageChanged={(page) => setPageNumber(page)}
                  onChangeRowsPerPage={onPageSizeChanged}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
